import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { api, absUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import { formatArticleDate } from "@/components/ArticleBody";
import { categoryPath } from "@/lib/paths";

export function ArticleCard({ a, large = false }) {
  const img = a.image_url ? absUrl(a.image_url) : "";
  return (
    <Link to={`/resources/${a.slug}`} className="group block bg-white border border-[#E8ECE9] hover:border-[#264839] transition-colors" data-testid={`article-card-${a.slug}`}>
      <div className={`${large ? "aspect-[16/8]" : "aspect-[16/9]"} bg-[#F0EFEA] overflow-hidden`}>
        {img ? <img src={img} alt={a.image_alt || a.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" /> : <div className="w-full h-full bg-[#11211A]/90" />}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#7D8C85]">
          {a.category?.name && <span className="text-[#B35C4A]" data-testid={`article-card-category-${a.slug}`}>{a.category.name}</span>}
          {a.category?.name && <span>·</span>}
          <time dateTime={a.published_at || ""} data-testid={`article-card-date-${a.slug}`}>{formatArticleDate(a.published_at)}</time>
        </div>
        <h2 className={`font-serif ${large ? "text-2xl md:text-3xl" : "text-xl"} text-[#11211A] mt-2 group-hover:text-[#264839]`} data-testid={`article-card-title-${a.slug}`}>{a.title}</h2>
        {a.excerpt && <p className="text-sm text-[#4A5550] mt-2 line-clamp-3">{a.excerpt}</p>}
        <div className="text-[11px] text-[#7D8C85] mt-3">By {a.author_name}</div>
      </div>
    </Link>
  );
}

export default function ResourcesPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/resources?limit=60").then(({ data }) => setData(data)).catch(() => setError("Couldn't load articles right now."));
  }, []);
  const items = data?.items || [];
  const [first, ...rest] = items;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="resources-page">
      <SEO title="Resources for Hotel Buyers — Guides & Supplier Insights" description="Practical guides for hotel procurement teams: choosing linen, towels, amenities and other suppliers, comparing quotes and getting better value. From Accommodation Suppliers." path="/resources" />
      <div className="max-w-3xl">
        <div className="tracking-overline text-[#B35C4A] mb-3">Resources</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#11211A] tracking-tight" data-testid="resources-heading">Guides for hotel buyers</h1>
        <p className="text-base md:text-lg text-[#4A5550] mt-4">Practical reading on sourcing suppliers, comparing quotes and getting better value for your property.</p>
      </div>

      {error && <div className="mt-10 text-sm text-[#B35C4A]" data-testid="resources-error">{error}</div>}
      {!error && !data && <div className="mt-10 text-sm text-[#7D8C85] animate-pulse">Loading articles…</div>}
      {data && items.length === 0 && (
        <div className="mt-10 bg-[#F0EFEA] p-12 text-center text-[#7D8C85]" data-testid="resources-empty">
          <div className="font-serif text-xl text-[#11211A]">Articles are on their way</div>
          <div className="text-sm mt-2">In the meantime, <Link to={categoryPath({ slug: "linen-textiles" })} className="text-[#264839] underline">browse hotel linen suppliers</Link> or <Link to="/get-quotes" className="text-[#264839] underline">request quotes</Link>.</div>
        </div>
      )}
      {first && (
        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-testid="resources-grid">
          <div className="lg:col-span-2"><ArticleCard a={first} large /></div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1 lg:col-span-1">{rest.slice(0, 2).map((a) => <ArticleCard key={a.id} a={a} />)}</div>
          {rest.slice(2).map((a) => <ArticleCard key={a.id} a={a} />)}
        </div>
      )}

      <div className="mt-14 bg-[#264839] text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6" data-testid="resources-rfq-strip">
        <div className="flex-1">
          <div className="tracking-overline text-[#E3B23C] mb-2">Ready to buy?</div>
          <div className="font-serif text-2xl md:text-3xl">Send one request, get quotes from top-ranked suppliers.</div>
          <div className="text-white/80 mt-2 text-sm inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Takes two minutes — free for buyers, always.</div>
        </div>
        <Link to="/get-quotes" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-[#264839] font-medium hover:bg-[#F0EFEA] transition-colors" data-testid="resources-rfq-cta">Request quotes <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}
